import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'

/**
 * Mobile-only floating CTA button (top-right, below navbar).
 * Appears after a short delay with an app-notification-like animation:
 * slides in from the right, starts as a small icon circle, then
 * expands to reveal the "Get a free quote" text, then collapses
 * back to the icon. Tapping opens the section's quote modal.
 */
const MobileQuoteButton = ({ onClick }) => {
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    // Appear after a brief delay so the page has time to load
    const showTimer = setTimeout(() => setIsVisible(true), 1500)
    return () => clearTimeout(showTimer)
  }, [])

  // When the button becomes visible, expand it briefly to show the text
  useEffect(() => {
    if (!isVisible) return
    // Expand after slide-in animation completes
    const expandTimer = setTimeout(() => setIsExpanded(true), 400)
    // Collapse back to icon after the user has time to read
    const collapseTimer = setTimeout(() => setIsExpanded(false), 4000)
    return () => {
      clearTimeout(expandTimer)
      clearTimeout(collapseTimer)
    }
  }, [isVisible])

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          onClick={onClick}
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          onHoverStart={() => setIsExpanded(true)}
          onHoverEnd={() => setIsExpanded(false)}
          onTap={() => setIsExpanded(true)}
          className="sm:hidden fixed top-24 right-4 z-40 flex items-center gap-2 bg-primary text-black font-bold shadow-lg rounded-full cursor-pointer hover:bg-yellow-400 transition-colors overflow-hidden"
          style={{ height: 48 }}
          aria-label={t('nav.quote', 'Get a free quote')}
        >
          {/* Icon (always visible) */}
          <motion.div
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: 48, height: 48 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-6 h-6"
            >
              <path
                fillRule="evenodd"
                d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625zM7.5 15a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5A.75.75 0 017.5 15zm.75 2.25a.75.75 0 000 1.5H12a.75.75 0 000-1.5H8.25z"
                clipRule="evenodd"
              />
              <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
            </svg>
          </motion.div>

          {/* Expanding text */}
          <AnimatePresence>
            {isExpanded && (
              <motion.span
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 'auto', opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="whitespace-nowrap text-sm tracking-wider uppercase pr-4 overflow-hidden"
              >
                {t('nav.quote', 'Get a free quote')}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      )}
    </AnimatePresence>
  )
}

export default MobileQuoteButton
